import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Home, KeyRound, Copy, ShieldOff, Upload, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import AdminLayout from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

const EMPTY = { first_name: "", last_name: "", email: "", phone: "", photo: "", bio: "", status: "attivo" };

export default function Owners() {
  const [owners, setOwners] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [open, setOpen] = useState(false);
  const [accessOwner, setAccessOwner] = useState(null);
  const [accessPwd, setAccessPwd] = useState("");
  const [creds, setCreds] = useState(null);
  const [accessLoading, setAccessLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/admin/owners").then((r) => setOwners(r.data));
  useEffect(() => { load(); }, []);

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("photo", data.url);
      toast.success("Foto caricata e ottimizzata");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Caricamento non riuscito");
    } finally {
      setUploading(false);
    }
  };

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (o) => { setEditing(o); setForm({ ...EMPTY, ...o }); setOpen(true); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openAccess = (o) => { setAccessOwner(o); setAccessPwd(""); setCreds(null); };

  const grantAccess = async () => {
    setAccessLoading(true);
    try {
      const { data } = await api.post(`/admin/owners/${accessOwner.id}/access`, { password: accessPwd || null });
      setCreds(data);
      toast.success("Accesso creato");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setAccessLoading(false); }
  };

  const revokeAccess = async () => {
    try {
      await api.delete(`/admin/owners/${accessOwner.id}/access`);
      toast.success("Accesso revocato");
      setAccessOwner(null);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const copyCreds = () => {
    navigator.clipboard.writeText(`Area Proprietario Quattro\nEmail: ${creds.email}\nPassword: ${creds.password}`);
    toast.success("Credenziali copiate");
  };

  const save = async () => {
    try {
      const payload = { first_name: form.first_name, last_name: form.last_name, email: form.email, phone: form.phone, photo: form.photo, bio: form.bio, status: form.status };
      if (editing) await api.put(`/admin/owners/${editing.id}`, payload);
      else await api.post("/admin/owners", payload);
      toast.success("Proprietario salvato");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (o) => {
    try {
      await api.delete(`/admin/owners/${o.id}`);
      toast.success("Proprietario eliminato");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <AdminLayout title="Gestione proprietari">
      <div className="flex justify-between items-center mb-6">
        <p className="text-brand-plum/60">{owners.length} proprietari nel network</p>
        <Button onClick={openNew} data-testid="owners-new-btn" className="rounded-full bg-brand-plum hover:bg-brand-violet text-white px-6"><Plus size={18} className="mr-1" /> Nuovo proprietario</Button>
      </div>

      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="owners-grid">
        {owners.map((o) => (
          <div key={o.id} className="rounded-2xl bg-white border border-brand-plum/8 shadow-sm p-6">
            <div className="flex items-center gap-4">
              <img src={o.photo || "https://i.pravatar.cc/150"} alt={o.first_name} className="w-16 h-16 rounded-full object-cover border-2 border-brand-teal" />
              <div className="min-w-0">
                <p className="font-serif text-lg text-brand-plum">{o.first_name} {o.last_name}</p>
                <p className="text-xs text-brand-plum/50 truncate">{o.email}</p>
                <p className="text-xs text-brand-teal flex items-center gap-1 mt-0.5"><Home size={12} /> {o.property_count} strutture</p>
              </div>
            </div>
            <div className="mt-3">
              {o.has_access ? (
                <span className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-brand-tealLight text-brand-tealDark" data-testid={`owner-access-badge-${o.id}`}><KeyRound size={11} /> Accesso attivo</span>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-slate-100 text-slate-500">Nessun accesso</span>
              )}
            </div>
            <p className="text-sm text-brand-plum/60 mt-3 line-clamp-2">{o.bio}</p>
            <div className="flex gap-2 mt-4">
              <Button onClick={() => openEdit(o)} variant="outline" size="sm" className="flex-1 border-brand-plum/15 text-brand-plum" data-testid={`owner-edit-${o.id}`}><Pencil size={14} className="mr-1" /> Modifica</Button>
              <Button onClick={() => openAccess(o)} variant="outline" size="sm" className="border-brand-teal/40 text-brand-tealDark hover:bg-brand-tealLight" data-testid={`owner-access-${o.id}`}><KeyRound size={14} className="mr-1" /> Accesso</Button>
              <Button onClick={() => remove(o)} variant="outline" size="sm" className="border-red-200 text-red-500 hover:bg-red-50"><Trash2 size={14} /></Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="owner-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-brand-plum">{editing ? "Modifica proprietario" : "Nuovo proprietario"}</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Nome</Label><Input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="owner-first-name" className="mt-1" /></div>
              <div><Label>Cognome</Label><Input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="owner-last-name" className="mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Email</Label><Input value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1" /></div>
              <div><Label>Telefono</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1" /></div>
            </div>
            <div>
              <Label>Foto proprietario</Label>
              <div className="flex items-center gap-4 mt-1.5">
                <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-brand-teal bg-brand-warm shrink-0 flex items-center justify-center">
                  {form.photo ? (
                    <img src={form.photo} alt="" className="w-full h-full object-cover" data-testid="owner-photo-preview" />
                  ) : (
                    <ImageIcon size={20} className="text-brand-plum/30" />
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <label className="inline-flex items-center gap-2 rounded-full bg-brand-plum hover:bg-brand-violet text-white text-sm font-medium px-4 h-10 cursor-pointer transition-colors" data-testid="owner-photo-upload-label">
                    <Upload size={15} /> {uploading ? "Caricamento..." : form.photo ? "Cambia foto" : "Carica foto"}
                    <input type="file" accept="image/*" className="hidden" data-testid="owner-photo-input" onChange={(e) => { uploadPhoto(e.target.files?.[0]); e.target.value = ""; }} />
                  </label>
                  {form.photo && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => set("photo", "")} className="text-red-500 hover:bg-red-50" data-testid="owner-photo-remove">Rimuovi</Button>
                  )}
                </div>
              </div>
              <p className="text-xs text-brand-plum/40 mt-1.5">La foto viene ridotta automaticamente per mantenere il sito veloce.</p>
            </div>
            <div><Label>Bio</Label><Textarea rows={3} value={form.bio} onChange={(e) => set("bio", e.target.value)} className="mt-1" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} data-testid="owner-save-btn" className="bg-brand-plum text-white">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!accessOwner} onOpenChange={(o) => !o && setAccessOwner(null)}>
        <DialogContent className="max-w-md" data-testid="owner-access-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-brand-plum">Accesso Area Proprietario</DialogTitle>
            <DialogDescription>{accessOwner?.first_name} {accessOwner?.last_name} · {accessOwner?.email || "nessuna email"}</DialogDescription>
          </DialogHeader>
          {!accessOwner?.email ? (
            <p className="text-sm text-red-500">Aggiungi prima un'email al proprietario per creare l'accesso.</p>
          ) : creds ? (
            <div className="space-y-3">
              <div className="rounded-xl bg-brand-warm border border-brand-plum/10 p-4 text-sm font-mono" data-testid="owner-generated-creds">
                <p className="text-brand-plum/60 text-xs mb-1">Email</p>
                <p className="text-brand-plum mb-3 break-all">{creds.email}</p>
                <p className="text-brand-plum/60 text-xs mb-1">Password</p>
                <p className="text-brand-plum break-all">{creds.password}</p>
              </div>
              <p className="text-xs text-brand-plum/50">Condividi queste credenziali con il proprietario. Accesso su <span className="font-mono">/owner/login</span>.</p>
              <Button onClick={copyCreds} variant="outline" className="w-full border-brand-teal/40 text-brand-tealDark"><Copy size={15} className="mr-1.5" /> Copia credenziali</Button>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-brand-plum/60">
                {accessOwner?.has_access ? "Reimposta la password per questo proprietario." : "Crea le credenziali di accesso. Lascia vuoto per generare una password automatica."}
              </p>
              <div><Label>Password {accessOwner?.has_access ? "(nuova)" : "(opzionale)"}</Label>
                <Input value={accessPwd} onChange={(e) => setAccessPwd(e.target.value)} data-testid="owner-access-password-input" className="mt-1" placeholder="Genera automaticamente se vuoto" /></div>
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            {accessOwner?.has_access && !creds && (
              <Button onClick={revokeAccess} variant="outline" className="border-red-200 text-red-500 hover:bg-red-50 sm:mr-auto" data-testid="owner-revoke-access-btn"><ShieldOff size={15} className="mr-1.5" /> Revoca accesso</Button>
            )}
            {creds ? (
              <Button onClick={() => setAccessOwner(null)} className="bg-brand-plum text-white">Chiudi</Button>
            ) : accessOwner?.email ? (
              <Button onClick={grantAccess} disabled={accessLoading} data-testid="owner-grant-access-btn" className="bg-brand-teal hover:bg-brand-tealDark text-brand-violet font-semibold">
                {accessLoading ? "Creazione..." : accessOwner?.has_access ? "Reimposta password" : "Crea accesso"}
              </Button>
            ) : (
              <Button variant="outline" onClick={() => setAccessOwner(null)}>Chiudi</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
